// Ticket Panel Component with per-item barber selection
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { 
  Trash2, Plus, Minus, CreditCard, Banknote, ArrowLeftRight, 
  X, Package, Check, Star, Split, User, Gift, Percent, Users
} from 'lucide-react';
import { useState } from 'react';
import SharedCommissionDialog from './SharedCommissionDialog';

export function TicketPanel({
  ticketPanelOpen,
  cart,
  barbers,
  selectedPayment,
  setSelectedPayment,
  updateQuantity,
  removeFromCart,
  updateItemBarber,
  updateItemDistribution,
  clearCart,
  getSubtotal,
  getTotal,
  handlePayClick,
  isProcessing,
  user,
  branches,
  selectedBranch,
  setSelectedBranch,
  loyaltyCode,
  setLoyaltyCode,
  loyaltyLoading,
  processLoyaltyCheckin,
  lastCheckin,
  setLastCheckin,
  onCombinedPayment,
  loyaltyRewards,
  selectedReward,
  setSelectedReward,
  discountAmount,
  setDiscountAmount,
}) {
  const [sharedDialog, setSharedDialog] = useState({ open: false, index: null });
  // Filter rewards that the user can afford
  const availableRewards = lastCheckin 
    ? loyaltyRewards.filter(r => r.points_required <= lastCheckin.total_points)
    : [];

  const handleRewardChange = (rewardId) => {
    if (rewardId === 'none') {
      setSelectedReward(null);
    } else {
      const reward = loyaltyRewards.find(r => r.id === rewardId);
      setSelectedReward(reward);
    }
  };

  const handleDiscountChange = (value) => {
    const numValue = parseFloat(value) || 0;
    const maxDiscount = getSubtotal ? getSubtotal() : 0;
    setDiscountAmount(Math.min(Math.max(0, numValue), maxDiscount));
  };

  const handleCloseLoyalty = () => {
    setLastCheckin(null);
    setLoyaltyCode('');
    setSelectedReward(null);
    setDiscountAmount(0);
  };

  return (
    <div className={`${ticketPanelOpen ? 'w-96' : 'w-0'} bg-zinc-900 border-l border-zinc-800 flex flex-col transition-all duration-300 overflow-hidden`}>
      {/* Ticket Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg text-white">Ticket</h2>
          {cart.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCart}
              className="text-zinc-400 hover:text-red-500"
              data-testid="pos-clear-cart"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        {user?.role === 'admin' && (
          <select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            className="mt-2 w-full h-10 bg-zinc-800 border border-zinc-700 text-white text-sm rounded-sm px-3"
            data-testid="pos-branch-select"
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Scrollable Content Area - From Loyalty to before Total */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea className="h-full">
          {/* Loyalty Check-in Section */}
          <div className="p-4 border-b border-zinc-800 bg-amber-500/5">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-4 h-4 text-gold" />
              <span className="text-sm text-gold font-medium">Programa de Lealtad</span>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={loyaltyCode}
                onChange={(e) => setLoyaltyCode(e.target.value.toUpperCase().slice(0, 3))}
                placeholder="ABC"
                maxLength={3}
                className="flex-1 h-10 bg-zinc-800 border border-zinc-700 text-white text-center font-mono uppercase tracking-widest rounded-sm px-3 placeholder:text-zinc-600 placeholder:font-normal placeholder:tracking-normal"
                data-testid="pos-loyalty-code-input"
                onKeyDown={(e) => e.key === 'Enter' && processLoyaltyCheckin()}
              />
              <Button
                onClick={processLoyaltyCheckin}
                disabled={loyaltyLoading || loyaltyCode.length !== 3}
                className="h-10 bg-gold text-black hover:bg-gold-dark px-4"
                data-testid="pos-loyalty-checkin-btn"
              >
                {loyaltyLoading ? '...' : <Check className="w-4 h-4" />}
              </Button>
            </div>
            {lastCheckin && (
              <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded-sm flex items-center justify-between">
                <p className="text-xs text-green-400">
                  ✓ {lastCheckin.user_name}: +{lastCheckin.points_added} pts ({lastCheckin.total_points} total)
                </p>
                <button
                  onClick={handleCloseLoyalty}
                  className="p-1 hover:bg-red-500/20 rounded transition-colors"
                  title="Cerrar programa de lealtad"
                >
                  <X className="w-4 h-4 text-red-400" />
                </button>
              </div>
            )}
          </div>

          {/* Discount Section - Only visible when loyalty user is checked in */}
          {lastCheckin && (
            <div className="p-4 border-b border-zinc-800 bg-purple-500/5">
              <div className="flex items-center gap-2 mb-3">
                <Gift className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-purple-400 font-medium">Aplicar Descuento</span>
              </div>
              
              {/* Reward Selector */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">
                    Recompensa a canjear
                  </label>
                  <Select 
                    value={selectedReward?.id || "none"} 
                    onValueChange={handleRewardChange}
                  >
                    <SelectTrigger 
                      className="h-10 bg-zinc-800 border-zinc-700 text-white"
                      data-testid="pos-reward-select"
                    >
                      <SelectValue placeholder="Seleccionar recompensa" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="none" className="text-zinc-400">
                        No aplica
                      </SelectItem>
                      {availableRewards.map((reward) => (
                        <SelectItem key={reward.id} value={reward.id} className="text-white">
                          <div className="flex items-center justify-between w-full">
                            <span>{reward.name}</span>
                            <span className="text-xs text-purple-400 ml-2">({reward.points_required} pts)</span>
                          </div>
                        </SelectItem>
                      ))}
                      {loyaltyRewards.filter(r => r.points_required > lastCheckin.total_points).length > 0 && (
                        <div className="px-2 py-1 text-xs text-zinc-500 border-t border-zinc-700">
                          Puntos insuficientes para otras recompensas
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  {selectedReward && (
                    <p className="text-xs text-purple-400 mt-1">
                      Se descontarán {selectedReward.points_required} pts del cliente
                    </p>
                  )}
                </div>

                {/* Discount Amount */}
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">
                    Monto a descontar
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      max={getSubtotal ? getSubtotal() : 0}
                      value={discountAmount || ''}
                      onChange={(e) => handleDiscountChange(e.target.value)}
                      placeholder="0.00"
                      className="h-10 bg-zinc-800 border-zinc-700 text-white pl-7 font-mono"
                      data-testid="pos-discount-amount"
                    />
                  </div>
                  {discountAmount > 0 && (
                    <p className="text-xs text-green-400 mt-1">
                      Descuento de ${discountAmount.toFixed(2)} aplicado
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Ticket Items */}
          <div className="p-4">
            {cart.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">Sin productos</p>
              </div>
            ) : (
              <div className="space-y-3">
              {cart.map((item, index) => (
                <div
                  key={`${item.item_id}-${index}`}
                  className={`bg-zinc-800/50 border rounded-sm p-3 animate-fade-in ${
                    !item.barber_id ? 'border-red-500/50' : 'border-zinc-800'
                  }`}
                  data-testid={`cart-item-${index}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-white text-sm font-medium">{item.name}</p>
                      <p className="text-zinc-500 text-xs">
                        ${item.price.toFixed(2)} c/u
                        {item.item_type === 'product' && (
                          <span className="ml-2 text-blue-400">(Producto)</span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => removeFromCart(index)}
                      className="text-zinc-500 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Barber selector for each item - REQUIRED */}
                  {barbers.length > 0 && (
                    <div className="mb-2 space-y-1.5">
                      {item.barber_distribution && item.barber_distribution.length >= 2 ? (
                        <button
                          onClick={() => setSharedDialog({ open: true, index })}
                          className="w-full bg-gold/10 border border-gold/40 rounded-sm px-2 py-1.5 text-left hover:bg-gold/20 transition-colors"
                          data-testid={`shared-summary-${index}`}
                        >
                          <div className="flex items-center gap-1.5">
                            <Users className="w-3 h-3 text-gold" />
                            <span className="text-xs text-gold font-medium">
                              {item.barber_distribution.length} barberos · pool {item.commission_pool_percentage}%
                            </span>
                          </div>
                          <div className="text-[10px] text-zinc-400 mt-0.5 truncate">
                            {item.barber_distribution.map(d => {
                              const b = barbers.find(bb => bb.id === d.barber_id);
                              return b ? `${b.name.split(' ')[0]} ${d.percentage}%` : '';
                            }).filter(Boolean).join(' · ')}
                          </div>
                        </button>
                      ) : (
                        <Select 
                          value={item.barber_id || "none"} 
                          onValueChange={(value) => updateItemBarber(index, value === "none" ? null : value)}
                        >
                          <SelectTrigger 
                            className={`h-8 text-xs text-white ${
                              !item.barber_id 
                                ? 'bg-red-500/10 border-red-500/50 ring-1 ring-red-500/30' 
                                : 'bg-zinc-700/50 border-zinc-600'
                            }`}
                            data-testid={`cart-item-barber-${index}`}
                          >
                            <div className="flex items-center gap-1">
                              <User className={`w-3 h-3 ${!item.barber_id ? 'text-red-400' : 'text-zinc-400'}`} />
                              <SelectValue placeholder="⚠️ Seleccionar barbero" />
                            </div>
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {barbers.map((barber) => (
                              <SelectItem key={barber.id} value={barber.id} className="text-white text-xs">
                                {barber.name} {barber.role_type === 'stylist' ? '(Estilista)' : barber.role_type === 'receptionist' ? '(Recepción)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {/* Share button — only for services */}
                      {item.item_type !== 'product' && updateItemDistribution && (
                        <button
                          onClick={() => setSharedDialog({ open: true, index })}
                          className="text-[10px] text-gold hover:underline flex items-center gap-1"
                          data-testid={`share-commission-btn-${index}`}
                        >
                          <Users className="w-3 h-3" />
                          {item.barber_distribution ? 'Editar distribución' : 'Compartir entre varios barberos'}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(index, -1)}
                        className="w-7 h-7 rounded-sm bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center text-white">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(index, 1)}
                        className="w-7 h-7 rounded-sm bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="text-gold font-semibold">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>

          {/* Payment Method Section */}
          <div className="p-4 border-t border-zinc-800">
            <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">
              Método de Pago
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'cash', icon: Banknote, label: 'Efectivo' },
                { id: 'card', icon: CreditCard, label: 'Tarjeta' },
                { id: 'transfer', icon: ArrowLeftRight, label: 'Transfer' },
                { id: 'combined', icon: Split, label: 'Combinado' },
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => {
                    if (id === 'combined') {
                      onCombinedPayment();
                    } else {
                      setSelectedPayment(id);
                    }
                  }}
                  className={`h-12 rounded-sm border flex items-center justify-center gap-2 transition-colors ${
                    selectedPayment === id
                      ? 'bg-gold/10 border-gold text-gold'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                  data-testid={`pos-payment-${id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-xs">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Fixed Bottom Section - Total and Pay Button */}
      <div className="p-4 border-t border-zinc-800 bg-zinc-900">
        {/* Totals Section */}
        <div className="py-3 space-y-2">
          {discountAmount > 0 && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-zinc-500 text-sm">Subtotal</span>
                <span className="text-zinc-400">${getSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-green-400 text-sm flex items-center gap-1">
                  <Percent className="w-3 h-3" />
                  Descuento
                </span>
                <span className="text-green-400">-${discountAmount.toFixed(2)}</span>
              </div>
            </>
          )}
          <div className="flex items-center justify-between pt-2">
            <span className="text-zinc-400 uppercase text-sm tracking-wide">Total</span>
            <span className="font-heading text-3xl text-gold">${getTotal().toFixed(2)}</span>
          </div>
        </div>

        <Button
          onClick={handlePayClick}
          disabled={cart.length === 0 || isProcessing}
          className="w-full h-14 bg-gold text-black hover:bg-gold-dark font-semibold uppercase tracking-wide text-lg rounded-sm"
          data-testid="pos-pay-btn"
        >
          {isProcessing ? 'Procesando...' : 'Cobrar'}
        </Button>
      </div>

      {/* Shared Commission Dialog */}
      {sharedDialog.open && sharedDialog.index != null && cart[sharedDialog.index] && (
        <SharedCommissionDialog
          open={sharedDialog.open}
          onClose={() => setSharedDialog({ open: false, index: null })}
          item={cart[sharedDialog.index]}
          barbers={barbers}
          initialDistribution={cart[sharedDialog.index].barber_distribution || []}
          initialPoolPct={cart[sharedDialog.index].commission_pool_percentage || 40}
          onSave={(distribution, poolPct) => {
            updateItemDistribution && updateItemDistribution(sharedDialog.index, distribution, poolPct);
            setSharedDialog({ open: false, index: null });
          }}
        />
      )}
    </div>
  );
}
